import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, Gauge, LineChart, Package, Server, TrendingUp, Users } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { CacheStatusIndicator } from '../components/CacheStatusIndicator';
import { TimeSeriesChart } from '../components/charts/TimeSeriesChart';
import { METRIC_COLORS, type ChartSeries } from '../components/charts/chartTypes';
import { StarchartToolbar } from '../components/starchart/StarchartToolbar';
import { StarchartMetricTabs } from '../components/starchart/StarchartMetricTabs';
import { ServerLeaderboardTable } from '../components/starchart/ServerLeaderboardTable';
import { ModpackShareCard } from '../components/starchart/ModpackShareCard';
import { ApiHealthCard } from '../components/starchart/ApiHealthCard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useStarchartRange } from '../hooks/useStarchartRange';
import { useStarchartLive } from '../hooks/useStarchartLive';
import { useStarchartNetwork } from '../hooks/useStarchartNetwork';
import { isAbortError, starchartApiService } from '../services/starchartApi';
import {
  computeSeriesStats,
  formatMspt,
  formatPlayers,
  formatPopularity,
  formatTps,
  normalizeSeries,
} from '../utils/starchart';
import type { ChartPoint } from '../utils/chartMath';
import type {
  NetworkMsptTimeSeriesResponse,
  NetworkPopularityTimeSeriesResponse,
  NetworkTimeSeriesResponse,
} from '../types/starchart';

/** Sparklines only need a day of shape, whatever range the main chart is showing. */
const SPARKLINE_WINDOW_MS = 24 * 3600_000;

interface NetworkSeriesState {
  players: NetworkTimeSeriesResponse | null;
  mspt: NetworkMsptTimeSeriesResponse | null;
  popularity: NetworkPopularityTimeSeriesResponse | null;
}

/**
 * StarChart network overview.
 *
 * Replaces the standalone site's Server List, Network Overview and Health tabs with one page,
 * and does it in about four upstream requests. The old site issued fifty-eight — one per server,
 * each pulling a full day of raw samples — purely to label a current player count that the live
 * status socket already publishes for free.
 */
export const StarchartView = () => {
  useDocumentTitle('StarChart');

  const range = useStarchartRange();
  const live = useStarchartLive(true);

  // A fixed sparkline window, floored to the hour, so it does not refetch as the main chart's
  // range changes and does not churn a new cache key every render.
  const sparklineRange = useMemo(() => {
    const end = Math.floor(Date.now() / 3600_000) * 3600_000;
    return { start: new Date(end - SPARKLINE_WINDOW_MS), end: new Date(end) };
  }, []);

  const network = useStarchartNetwork(
    live,
    range.popularityWindow,
    sparklineRange.start,
    sparklineRange.end,
    '15m',
  );

  const [series, setSeries] = useState<NetworkSeriesState>({
    players: null,
    mspt: null,
    popularity: null,
  });
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const { fetchStart, fetchEnd, resolution, metric, popularityWindow } = range;

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    setSeriesLoading(true);
    setSeriesError(null);

    const request =
      metric === 'popularity'
        ? starchartApiService
            .getNetworkPopularity(popularityWindow, fetchStart, fetchEnd, { signal })
            .then((popularity) => ({ popularity }))
        : metric === 'players'
          ? starchartApiService
              .getNetworkTimeseries(fetchStart, fetchEnd, resolution, { signal })
              .then((players) => ({ players }))
          : starchartApiService
              .getNetworkMspt(fetchStart, fetchEnd, resolution, { signal })
              .then((mspt) => ({ mspt }));

    request
      .then((patch) => {
        if (signal.aborted) return;
        setSeries((prev) => ({ ...prev, ...patch }));
        setSeriesLoading(false);
      })
      .catch((err) => {
        // A cancelled request is not a failure; painting an error over good data is the classic
        // bug in this pattern.
        if (isAbortError(err) || signal.aborted) return;
        setSeriesError(err instanceof Error ? err.message : 'Failed to load network history');
        setSeriesLoading(false);
      });

    return () => controller.abort();
  }, [metric, popularityWindow, fetchStart, fetchEnd, resolution]);

  const chartSeries = useMemo<ChartSeries[]>(() => {
    let points: ChartPoint[] = [];
    let label = 'Network players';
    let color: string = METRIC_COLORS.players;

    if (metric === 'players') {
      points = (series.players?.data ?? []).map((p) => ({ t: p.timestamp * 1000, v: p.player_count }));
    } else if (metric === 'tps') {
      label = 'Network TPS';
      color = METRIC_COLORS.tps;
      points = (series.mspt?.data ?? []).map((p) => ({ t: p.timestamp * 1000, v: p.tps }));
    } else if (metric === 'mspt') {
      label = 'Network MSPT';
      color = METRIC_COLORS.mspt;
      points = (series.mspt?.data ?? []).map((p) => ({ t: p.timestamp * 1000, v: p.mspt }));
    } else {
      label = `Network popularity (${popularityWindow})`;
      color = METRIC_COLORS.popularity;
      points = (series.popularity?.data ?? []).map((p) => ({
        t: p.timestamp * 1000,
        v: p.popularity_index,
      }));
    }

    if (points.length === 0) return [];

    return [
      {
        id: 'network',
        label,
        color,
        kind: 'area',
        points: normalizeSeries(points, range.normalization, range.movingAverageWindow),
      },
    ];
  }, [metric, popularityWindow, series, range.normalization, range.movingAverageWindow]);

  const networkStats = useMemo(
    () => computeSeriesStats(chartSeries[0]?.points ?? []),
    [chartSeries],
  );

  const liveTps = useMemo(() => {
    const values: number[] = [];
    for (const status of live.values()) {
      if (!status.isOffline && isFinite(status.tps)) values.push(status.tps);
    }
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
  }, [live]);

  const valueFormat = useCallback(
    (value: number) => {
      if (range.normalization !== 'none') return value.toFixed(2);
      if (metric === 'mspt') return formatMspt(value);
      if (metric === 'tps') return formatTps(value);
      if (metric === 'popularity') return formatPopularity(value);
      return formatPlayers(value);
    },
    [metric, range.normalization],
  );

  const chartTitle =
    metric === 'players'
      ? 'Players across the network'
      : metric === 'tps'
        ? 'Average ticks per second'
        : metric === 'mspt'
          ? 'Average milliseconds per tick'
          : `Average popularity index (${popularityWindow})`;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Infrastructure"
        title="StarChart"
        description="Player counts, tick performance and popularity across every PlayCDU server."
        icon={LineChart}
        meta={
          <>
            <span className="inline-flex items-center gap-1.5 text-xs text-glass-400">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" aria-hidden="true" />
              Live
            </span>
            <CacheStatusIndicator
              cachedAt={network.loadedAt}
              onRefresh={network.refresh}
              ttl={5 * 60_000}
            />
          </>
        }
      >
        <StarchartToolbar range={range} />
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Players now"
          value={formatPlayers(network.totalLivePlayers)}
          icon={Users}
          tone="accent"
          hint="Live now"
        />
        <StatTile
          label="Range peak"
          value={formatPlayers(networkStats.max)}
          icon={TrendingUp}
          tone="success"
          hint={`Average ${formatPlayers(networkStats.average)}`}
        />
        <StatTile
          label="Servers"
          value={network.activeServerCount}
          icon={Server}
          hint={`${network.rows.length} tracked`}
        />
        <StatTile
          label="Modpacks"
          value={network.activeModpackCount}
          icon={Package}
          hint={liveTps !== null ? `${formatTps(liveTps)} average TPS` : undefined}
        />
      </div>

      {network.error ? (
        <EmptyState
          tone="danger"
          icon={Server}
          title="StarChart is unreachable"
          description={network.error}
          action={
            <button type="button" className="btn-primary" onClick={network.refresh}>
              Retry
            </button>
          }
        />
      ) : (
        <>
          <div className="space-y-3">
            <StarchartMetricTabs
              metric={metric}
              onMetricChange={range.setMetric}
              popularityWindow={popularityWindow}
              onPopularityWindowChange={range.setPopularityWindow}
            />

            {seriesError ? (
              <EmptyState
                tone="danger"
                icon={LineChart}
                title="Could not load this metric"
                description={seriesError}
              />
            ) : (
              <TimeSeriesChart
                series={chartSeries}
                title={chartTitle}
                icon={metric === 'mspt' ? Cpu : metric === 'tps' ? Gauge : LineChart}
                isLoading={seriesLoading && chartSeries.length === 0}
                timeZone={range.timeZone}
                valueFormat={valueFormat}
                downsample={metric === 'mspt' || metric === 'tps' ? 'minmax' : 'lttb'}
                referenceLines={
                  metric === 'tps' && range.normalization === 'none'
                    ? [{ value: 20, label: '20 TPS', color: 'var(--tone-success-text)' }]
                    : undefined
                }
                onBrush={range.zoomTo}
                emptyMessage="No network history for this range."
              />
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2">
              <div className="glass-card p-4 md:p-5">
                <SectionHeader
                  title="Servers"
                  icon={Server}
                  count={network.rows.length}
                  description="Live counts joined to StarChart history and modpack metadata."
                />
                <div className="mt-3">
                  <ServerLeaderboardTable
                    rows={network.rows}
                    popularityWindow={popularityWindow}
                    loading={network.loading}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <ModpackShareCard
                distribution={network.distribution}
                totalPlayers={network.totalLivePlayers}
              />
              <ApiHealthCard dbInfo={network.dbInfo} />
            </div>
          </div>

          <p className="text-xs text-glass-500">
            Looking for a server that is not listed?{' '}
            <Link to="/status-monitor" className="link">
              Status monitor
            </Link>{' '}
            shows every reporting instance, including those StarChart has no history for.
          </p>
        </>
      )}
    </div>
  );
};
